import type { TextInputProps } from './TextInput';
import { TextInput } from './TextInput';
import type { CommonInputProps } from './CommonInputProps';
import React, { useCallback, useEffect, useState } from 'react';
import { isEmptyString } from '../../util';

const CURRENT_LANGUAGE: string = window.SenseConstants?.language ?? 'en-US';
const NUMBER_SAMPLE = 123456.789;

/**
 * Props for a {@link NumberInput} component.
 */
export interface NumberInputProps
  extends Omit<
      TextInputProps,
      | 'value'
      | 'onChange'
      | 'defaultValue'
      | 'inputMode'
      | 'type'
      | 'allowShowPassword'
      | 'multiline'
      | 'multilineRows'
    >,
    CommonInputProps<number | undefined> {
  /**
   * maximal decimal point digits allowed in the input
   */
  decimalPlaces?: number;
  /**
   * flag for using localized number format
   */
  intled?: boolean;
}

function decimalCount(num: number) {
  return num.toString().split('.')[1]?.length ?? 0;
}

const parseNumber = (
  number: string,
  places: number
): number | undefined | 'overflow' => {
  const parts = Intl.NumberFormat(CURRENT_LANGUAGE).formatToParts(
    NUMBER_SAMPLE
  );

  const groupSymbol = parts.find(el => el.type === 'group')?.value;
  const decimalSymbol = parts.find(el => el.type === 'decimal')?.value;

  if (groupSymbol) {
    number = number.replace(new RegExp('\\' + groupSymbol, 'g'), '');
  }

  if (decimalSymbol) {
    number = number.replace(decimalSymbol, '.');
  }

  // checks if user wants to type unwanted zeros
  if (number.includes('.') && number[number.indexOf('.') + places + 1] == '0') {
    return 'overflow';
  }
  return isNaN(Number(number)) ? undefined : Number(number);
};

/**
 * @param value max value is 9007199254740991 (MAX_SAFE_INTEGER), bigger number is casted to Scientific notation
 * @param onChange on change handler
 * @param defaultValue field default value
 * @param decimalPlaces number of decimal places that are allowed
 * @param intled flag for using localized number format
 * @param props leftover @{link NumberInputProps}
 */
export function NumberInput({
  onChange,
  value,
  defaultValue,
  decimalPlaces = 0,
  intled = false,
  ...props
}: Readonly<NumberInputProps>): JSX.Element {
  const [currIntlNumber, setCurrIntlNumber] = useState(
    value === 0 || value ? value : ''
  );
  const [currentlabel, setCurrentLabel] = useState(props.label);

  useEffect(() => {
    if (value === undefined || value === null) {
      setCurrIntlNumber('');
    } else {
      setCurrIntlNumber(value);
    }
  }, [value]);

  const changeHandlerIntl = useCallback(
    intlNumber => {
      const parsedValue = parseNumber(intlNumber, decimalPlaces);
      if (parsedValue == 'overflow') {
        return;
      }
      if (parsedValue && decimalCount(parsedValue) > decimalPlaces) {
        return;
      }
      setCurrIntlNumber(intlNumber);
      if (isEmptyString(intlNumber)) {
        onChange?.(undefined);
      } else {
        onChange?.(Number(parsedValue));
      }
    },
    [onChange, decimalPlaces]
  );

  const changeHandler = useCallback(
    inputValue => {
      const numInputValue = Number(inputValue);
      // check if user wants to use decimal point if not allowed
      if (inputValue.includes('.') === true && decimalPlaces === 0) {
        return;
      }
      if (isNaN(numInputValue)) {
        return;
      }
      if (isEmptyString(inputValue)) {
        setCurrIntlNumber('');
        onChange?.(undefined);
      } else {
        if (
          numInputValue <= Number.MAX_SAFE_INTEGER &&
          numInputValue >= Number.MIN_SAFE_INTEGER
        ) {
          if (
            inputValue.includes('.') &&
            inputValue.split('.')[1].length > decimalPlaces
          ) {
            return;
          }
          setCurrIntlNumber(inputValue);
          onChange?.(numInputValue);
        } else {
          return;
        }
      }
    },
    [onChange, decimalPlaces]
  );

  return intled ? (
    <TextInput
      {...props}
      type="text"
      label={currentlabel}
      defaultValue={defaultValue?.toString()}
      onFocus={() => setCurrentLabel(props.label)}
      onBlur={() => setCurrentLabel(props.label)}
      value={String(currIntlNumber)}
      onChange={changeHandlerIntl}
      inputMode="decimal"
    />
  ) : (
    <TextInput
      {...props}
      type="text"
      inputMode="numeric"
      defaultValue={defaultValue?.toString()}
      value={String(currIntlNumber)}
      onChange={changeHandler}
      labelOnTop={props.labelOnTop}
    />
  );
}
